// src/components/TemplatePlantillaPro/EmailPanel.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import api from "../../auth/api";

let DOMPurifyModule = null;

const fromConfig =
  (window.__APP_CONFIG__ && window.__APP_CONFIG__.API_BASE) || "";
const fromVite =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  "";
const fromCRA = process.env.REACT_APP_API_BASE || "";
const trimSlash = (s) => (s || "").replace(/\/+$/, "");
const API_BASE = trimSlash(fromConfig || fromVite || fromCRA) || "";

export default function EmailPanel({
  sendEmail,
  setSendEmail,
  templateEmailSubject,
  setTemplateEmailSubject,
  templateEmailBody,
  setTemplateEmailBody,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mediaImages, setMediaImages] = useState([]);

  // Preview controls
  const [theme, setTheme] = useState("dark"); // "dark" | "light"
  const [fontScale, setFontScale] = useState(1); // 0.75 .. 1.5

  // Estado para saber si logramos cargar dompurify dinámicamente
  const [domPurifyLoaded, setDomPurifyLoaded] = useState(false);

  // Intentar cargar dompurify de forma dinámica (no rompe la compilación si no existe)
  useEffect(() => {
    import("dompurify")
      .then((mod) => {
        DOMPurifyModule = mod.default || mod;
        setDomPurifyLoaded(true);
      })
      .catch(() => {
        // no instalado — queda false y usamos fallback
        console.warn(
          "dompurify no disponible — usando fallback de sanitización (menos seguro)"
        );
      });
  }, []);

  // Cargar lista de imágenes ya subidas
  const loadMedia = async () => {
    try {
      const res = await api.get("/media/email/list");
      if (res.data?.ok && Array.isArray(res.data.images)) {
        setMediaImages(res.data.images);
      }
    } catch (err) {
      console.error("Error cargando imágenes:", err);
    }
  };

  useEffect(() => {
    loadMedia();
  }, []);

  // Subir imagen
  const handleUploadImage = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);

      const fd = new FormData();
      fd.append("file", file);

      const res = await api.post("/media/email", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!res.data?.ok) {
        alert(res.data?.error || "Error subiendo la imagen");
        return;
      }

      const raw = res.data.url || res.data.path;
      if (!raw) {
        alert("No se recibió la URL de la imagen");
        return;
      }

      const url = raw.startsWith("http") ? raw : `${API_BASE}${raw}`;

      alert(`Imagen subida:\n${url}`);
      await loadMedia();
    } catch (err) {
      console.error(err);
      alert("Error subiendo la imagen. Intenta de nuevo.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Copiar URL al portapapeles
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      // opcional: aviso pequeño
    } catch {
      alert("No se pudo copiar automáticamente. Copia la URL manualmente.");
    }
  };

  // Sanitizar el HTML del correo — usa DOMPurify si está cargado, si no usa un fallback regex (temporal)
  const safeHtml = useMemo(() => {
    const raw =
      templateEmailBody && templateEmailBody.trim()
        ? templateEmailBody
        : "<p>(Sin contenido)</p>";

    if (domPurifyLoaded && DOMPurifyModule && typeof DOMPurifyModule.sanitize === "function") {
      return DOMPurifyModule.sanitize(raw, { ADD_ATTR: ["target"] });
    }

    // FALLBACK SIMPLE (NO 100% SEGURO): eliminamos <script> y atributos on*.
    // Úsalo solo temporalmente mientras instalas dompurify.
    return raw
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "");
  }, [templateEmailBody, domPurifyLoaded]);

  // Construir srcDoc para el iframe con estilos base
  const srcDoc = useMemo(() => {
    const baseStyles = `
          /* Reset simple para emails */
          html,body{margin:0;padding:16px;background:${theme === "dark" ? "#020617" : "#ffffff"};color:${theme === "dark" ? "#e5e7eb" : "#1f2937"};font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial;box-sizing:border-box;}
          img{max-width:100%;height:auto;}
          a{color:${theme === "dark" ? "#60a5fa" : "#1d4ed8"};word-break:break-word;}
          p, div, span, td, th{font-size:${(fontScale * 14).toFixed(0)}px;line-height:1.4;color:inherit;}
          /* Contenedor del mensaje para centrar y simular ancho de cliente */
          /* AUMENTADO: max-width de 680px -> 840px (ajusta si quieres otro valor) */
          .email-container{max-width:840px /* antes 680px */;margin:0 auto;background:${theme === "dark" ? "#041025" : "#ffffff"};padding:20px;border-radius:6px;border:1px solid ${theme === "dark" ? "rgba(255,255,255,0.03)" : "#e6e6e6"};box-shadow: 0 6px 18px rgba(2,6,23,0.25);}
          /* Forzar estilos legibles para tablas típicas de email */
          table{border-collapse:collapse;width:100%;}
          table td{padding:6px 0;}
          /* Evitar que elementos del email sobresalgan visualmente */
          *{box-sizing:border-box;}
        `;

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <meta name="viewport" content="width=device-width,initial-scale=1"/>
          <style>${baseStyles}</style>
        </head>
        <body>
          <div class="email-container">
            ${safeHtml}
          </div>
        </body>
      </html>
    `;
  }, [safeHtml, theme, fontScale]);

  return (
    <div
      style={{
        display: "grid",
        gap: 12,
        padding: 12,
        borderRadius: 6,
        border: "1px solid #1f2937",
        background: "#020817",
        fontSize: 12,
        color: "#e5e7eb",
      }}
    >
      {/* Activar envío por email */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <input
          type="checkbox"
          checked={!!sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <span>Enviar también por Email</span>
      </label>

      {/* Asunto */}
      <div>
        <div style={{ marginBottom: 4, color: "#9ca3af" }}>
          Asunto del correo
        </div>
        <input
          value={templateEmailSubject}
          onChange={(e) => setTemplateEmailSubject(e.target.value)}
          placeholder="Ej: Recordatorio de cita para {nombre}"
          style={{
            width: "100%",
            padding: "7px 9px",
            borderRadius: 4,
            border: "1px solid #374151",
            background: "#020817",
            color: "#e5e7eb",
            fontSize: 12,
          }}
        />
      </div>

      {/* Subir imagen */}
      <div>
        <div style={{ marginBottom: 4, color: "#9ca3af" }}>
          Subir imagen (para usar en el HTML)
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadImage}
            disabled={uploading}
            style={{ fontSize: 11, color: "#9ca3af" }}
          />
          <span style={{ fontSize: 10, color: "#6b7280" }}>
            Se guarda en /media/email/... Usa esa URL en tu HTML.
          </span>
        </div>
      </div>

      {/* Lista de imágenes */}
      {mediaImages.length > 0 && (
        <div>
          <div style={{ marginBottom: 4, color: "#9ca3af" }}>
            Imágenes disponibles
          </div>
          <div
            style={{
              display: "grid",
              gap: 6,
              maxHeight: 160,
              overflowY: "auto",
              padding: 6,
              borderRadius: 4,
              border: "1px solid #111827",
              background: "#020617",
            }}
          >
            {mediaImages.map((img) => {
              const url =
                img.url ||
                (img.path ? `${API_BASE}${img.path}` : "");
              if (!url) return null;

              return (
                <div
                  key={img.filename || url}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "56px 1fr auto",
                    gap: 6,
                    alignItems: "center",
                    padding: 4,
                    borderRadius: 4,
                    border: "1px solid #111827",
                  }}
                >
                  <img
                    src={url}
                    alt={img.filename}
                    style={{
                      width: 52,
                      height: 36,
                      objectFit: "cover",
                      borderRadius: 3,
                    }}
                  />
                  <input
                    readOnly
                    value={url}
                    onFocus={(e) => e.target.select()}
                    style={{
                      width: "100%",
                      padding: "3px 5px",
                      fontSize: 9,
                      borderRadius: 3,
                      border: "1px solid #1f2937",
                      background: "#020817",
                      color: "#9ca3af",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(url)}
                    style={{
                      fontSize: 9,
                      padding: "3px 6px",
                      borderRadius: 3,
                      border: "1px solid #374151",
                      background: "#020817",
                      color: "#9ca3af",
                      cursor: "pointer",
                    }}
                  >
                    Copiar
                  </button>
                </div>
              );
            })}
          </div>
          <div
            style={{
              fontSize: 9,
              color: "#6b7280",
              marginTop: 3,
            }}
          >
            Usa en tu plantilla:
            {` <img src="URL_DE_LA_IMAGEN" alt="" />`}
          </div>
        </div>
      )}

      {/* HTML del correo */}
      <div>
        <div style={{ marginBottom: 4, color: "#9ca3af" }}>
          HTML del correo
        </div>
        <textarea
          value={templateEmailBody || ""}
          onChange={(e) => setTemplateEmailBody(e.target.value)}
          rows={12}
          placeholder={`Pega aquí el HTML completo del correo.\nEjemplo:\n<table width="600">...\n  <img src="https://TU_API/media/email/archivo.png" />\n...</table>`}
          style={{
            width: "100%",
            padding: 8,
            borderRadius: 4,
            border: "1px solid #374151",
            background: "#020817",
            color: "#e5e7eb",
            fontFamily: "monospace",
            fontSize: 11,
            resize: "vertical",
          }}
        />
      </div>

      {/* Vista previa mejorada */}
      <div>
        <div style={{ marginBottom: 8, color: "#9ca3af", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>Vista previa</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => setTheme("dark")}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: theme === "dark" ? "1px solid #60a5fa" : "1px solid rgba(255,255,255,0.04)",
                  background: theme === "dark" ? "#021028" : "#fff",
                  cursor: "pointer",
                }}
              >
                Oscuro
              </button>
              <button
                onClick={() => setTheme("light")}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: theme === "light" ? "1px solid #1d4ed8" : "1px solid #e5e7eb",
                  background: theme === "light" ? "#f8fafc" : "#fff",
                  cursor: "pointer",
                }}
              >
                Claro
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 11, color: "#9ca3af" }}>Tamaño</label>
              <input
                type="range"
                min="0.75"
                max="1.5"
                step="0.05"
                value={fontScale}
                onChange={(e) => setFontScale(Number(e.target.value))}
                style={{ width: 120 }}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.04)",
            background: theme === "dark" ? "#020617" : "#f3f4f6",
            padding: 8,
            maxHeight: 520, // aumenta la altura si lo deseas
            boxShadow: "0 6px 18px rgba(2,6,23,0.35)",
            overflow: "auto", // permite scroll si es necesario
          }}
        >
          {/* Contenedor que permite hacer scroll horizontal si el modal es angosto */}
          <div style={{ width: "100%", overflowX: "auto", paddingBottom: 8 }}>
            <iframe
              title="email-preview"
              sandbox="allow-same-origin allow-popups allow-forms"
              srcDoc={srcDoc}
              // style: width 100% del contenedor scrollable, pero el email interno (.email-container) controla su max-width
              style={{
                width: "100%",
                minWidth: 840,     // fuerza un ancho mínimo para mostrar la plantilla amplia
                height: 480,
                border: "none",
                borderRadius: 4,
                background: "transparent",
              }}
            />
            {/*
      Si prefieres que el iframe sea totalmente responsivo y sin minWidth,
      reemplaza minWidth: 840 por minWidth: "100%" o elimina esa línea.
      También puedes usar minWidth: 1000 para ver plantilla aún más ancha.
    */}
          </div>
        </div>

      </div>
    </div>
  );
}
